import { useCallback, useEffect, useRef, useState } from "react";
import Lottie from "lottie-react";
import starsAndLightAnimationData from "../assets/starsandlight.json";
import { API_URL } from "../config/api";
import {
  GAME_LEVELS,
  getMasteryThresholdsForLevel,
} from "../constants/gameLevels";
import LevelHistoryView from "./LevelHistoryView";
import HistoryFilterDropdown from "./HistoryFilterDropdown";
import { fetchLevelHistory, markLevelHistoryAsRead, NOTIFICATIONS_CHANGED_EVENT } from "../utils/levelHistoryApi";
import { syncDailyQuestHistoryIfNeeded } from "../utils/questCompletionHandler";
import {
  filterHistoryEntries,
  readHistoryFilter,
} from "../constants/levelHistory";
import "./VocabTooltip.css";
import "./MasteryProgressView.css";

const STATS_PANEL_LS_KEY = "vocabularium_mastery_stats_panel";

function readStatsPanel() {
  try {
    const raw = localStorage.getItem(STATS_PANEL_LS_KEY);
    const value = Number(raw);
    return Number.isInteger(value) && value >= 0 && value <= 2 ? value : 1;
  } catch {
    return 1;
  }
}

function writeStatsPanel(index) {
  try {
    localStorage.setItem(STATS_PANEL_LS_KEY, String(index));
  } catch {
    // quota / mode privé : on ignore
  }
}

const publicUrl = process.env.PUBLIC_URL || "";

// Icônes identiques à celles du tableau des détails du mot (colonne Résultat).
function CircleAlert({ className = "", style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

function CircleCheck({ className = "", style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <path d="M21.801 10A10 10 0 1 1 17 3.335" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}

function ThresholdStatusIcon({ validated }) {
  return validated ? (
    <CircleCheck
      className="mastery-progress__threshold-icon"
      style={{ color: "#166534" }}
    />
  ) : (
    <CircleAlert
      className="mastery-progress__threshold-icon"
      style={{ color: "#991b1b" }}
    />
  );
}

function computeCurrentLevelProgress(poolSize, discoveredCount, masteryPercent, thresholds) {
  if (poolSize <= 0) return 0;

  const discoveryRate = discoveredCount / poolSize;
  const masteryRate = masteryPercent / 100;
  const discoveryProgress = discoveryRate / thresholds.discovery;
  const masteryProgress = masteryRate / thresholds.mastery;

  return Math.min(1, Math.min(discoveryProgress, masteryProgress));
}

function computeOverallProgress(validatedLevelCount, currentLevelProgress) {
  const total = GAME_LEVELS.length;
  const clampedValidated = Math.max(0, Math.min(validatedLevelCount, total));
  return Math.min(1, (clampedValidated + currentLevelProgress) / total);
}

function getCurrentLevelId(validatedLevelCount) {
  if (validatedLevelCount >= GAME_LEVELS.length) {
    return GAME_LEVELS[GAME_LEVELS.length - 1].id;
  }
  return validatedLevelCount + 1;
}

function getLevelState(levelId, validatedLevelCount, currentLevelId) {
  if (levelId <= validatedLevelCount) return "validated";
  if (levelId === currentLevelId) return "current";
  return "locked";
}

function getMiniBarFillStyle(percent) {
  const p = Math.max(0, Math.min(100, percent));
  const hue = Math.round((p / 100) * 120);
  const dark = `hsl(${hue}, 58%, 34%)`;
  const light = `hsl(${hue}, 72%, 46%)`;

  return {
    width: `${p}%`,
    background: `linear-gradient(90deg, ${dark} 0%, ${light} 100%)`,
  };
}

function MiniStatBar({ value, max = 100, label }) {
  const percent = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;

  return (
    <div
      className="mastery-progress__mini-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <div className="mastery-progress__mini-bar-fill" style={getMiniBarFillStyle(percent)} />
    </div>
  );
}

function StatsTwoColumns({ left, right }) {
  return (
    <>
      <div className="mastery-progress__stats-col">{left}</div>
      <div className="mastery-progress__stats-separator" aria-hidden="true" />
      <div className="mastery-progress__stats-col">{right}</div>
    </>
  );
}

function StatSecondarySlot({ children = null, tall = false }) {
  return (
    <div
      className={`mastery-progress__stat-slot-secondary${
        tall ? " mastery-progress__stat-slot-secondary--tall" : ""
      }`}
    >
      {children || <span className="mastery-progress__stat-slot-placeholder" aria-hidden="true" />}
    </div>
  );
}

function GradeChipGrid({ grades = [], singleColumn = false }) {
  return (
    <div
      className={`mastery-progress__grade-grid${
        singleColumn ? " mastery-progress__grade-grid--single" : ""
      }`}
    >
      {grades.map(({ id, value }) => (
        <div
          key={id}
          className={`mastery-progress__grade-chip${
            value === 0 ? " mastery-progress__grade-chip--empty" : ""
          }`}
        >
          <span className="mastery-progress__grade-chip-label">{id}</span>
          <span className="mastery-progress__grade-chip-value">{value}</span>
        </div>
      ))}
    </div>
  );
}

function MasteredGradeBreakdown({ counts = {} }) {
  const grades = [
    { id: "A+", value: counts["A+"] ?? 0 },
    { id: "A", value: counts.A ?? 0 },
    { id: "A-", value: counts["A-"] ?? 0 },
    { id: "B+", value: counts["B+"] ?? 0 },
  ];

  return <GradeChipGrid grades={grades} />;
}

function NoEwssBreakdown({ counts = {} }) {
  const rows = [
    {
      label: "Mots jamais joués (dans le pool)",
      value: counts.neverPlayedInPool ?? 0,
    },
    {
      label: "Mots jamais joués (noyau actif)",
      value: counts.neverPlayedInNoyau ?? 0,
    },
    { label: "Mots joués 1 fois", value: counts.playedOnce ?? 0 },
    { label: "Mots joués 2 fois", value: counts.playedTwice ?? 0 },
  ];

  return (
    <div className="mastery-progress__detail-list">
      {rows.map(({ label, value }) => (
        <div key={label} className="mastery-progress__stat-row">
          <span className="mastery-progress__stat-label">{label}</span>
          <span className="mastery-progress__stat-value">{value}</span>
        </div>
      ))}
    </div>
  );
}

function sumReviewGradeCounts(counts = {}) {
  const keys = ["B", "B-", "C+", "C", "C-", "D+", "D", "D-"];
  return keys.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
}

function ReviewGradeBreakdown({ counts = {} }) {
  const leftGrades = [
    { id: "B", value: counts.B ?? 0 },
    { id: "B-", value: counts["B-"] ?? 0 },
    { id: "C+", value: counts["C+"] ?? 0 },
    { id: "C", value: counts.C ?? 0 },
  ];
  const rightGrades = [
    { id: "C-", value: counts["C-"] ?? 0 },
    { id: "D+", value: counts["D+"] ?? 0 },
    { id: "D", value: counts.D ?? 0 },
    { id: "D-", value: counts["D-"] ?? 0 },
  ];

  return (
    <div className="mastery-progress__review-grade-columns">
      <GradeChipGrid grades={leftGrades} singleColumn />
      <GradeChipGrid grades={rightGrades} singleColumn />
    </div>
  );
}

function Skel({ className = "", style }) {
  return <span className={`mastery-progress-skel ${className}`.trim()} style={style} />;
}

function SkeletonMiniBar() {
  return <div className="mastery-progress__mini-bar" />;
}

function SkeletonStatRow({ labelWidth, valueWidth }) {
  return (
    <div className="mastery-progress__stat-row">
      <span className="mastery-progress__stat-label">
        <Skel className="mastery-progress-skel--stat-label" style={{ width: labelWidth }} />
        {"\u00a0"}
      </span>
      <span className="mastery-progress__stat-value">
        <Skel className="mastery-progress-skel--stat-value" style={{ width: valueWidth }} />
        {"\u00a0"}
      </span>
    </div>
  );
}

function SkeletonGradeChips({ count, singleColumn = false }) {
  return (
    <div
      className={`mastery-progress__grade-grid${
        singleColumn ? " mastery-progress__grade-grid--single" : ""
      }`}
    >
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="mastery-progress__grade-chip">
          <Skel className="mastery-progress-skel--chip-label" />
          <Skel className="mastery-progress-skel--chip-value" />
        </div>
      ))}
    </div>
  );
}

function SkeletonStatsPanel({ panel }) {
  if (panel === 0) {
    return (
      <StatsTwoColumns
        left={
          <>
            <h3 className="mastery-progress__stats-title">
              <Skel className="mastery-progress-skel--stats-title" style={{ width: "148px" }} />
            </h3>
            <SkeletonStatRow labelWidth="118px" valueWidth="72px" />
            <SkeletonMiniBar />
            <StatSecondarySlot>
              <SkeletonStatRow labelWidth="128px" valueWidth="28px" />
            </StatSecondarySlot>
            <p className="mastery-progress__stat-hint">
              <Skel className="mastery-progress-skel--hint" style={{ width: "196px" }} />
              {"\u00a0"}
            </p>
          </>
        }
        right={
          <>
            <h3 className="mastery-progress__stats-title">
              <Skel className="mastery-progress-skel--stats-title" style={{ width: "132px" }} />
            </h3>
            <SkeletonStatRow labelWidth="112px" valueWidth="72px" />
            <SkeletonMiniBar />
            <StatSecondarySlot tall>
              <SkeletonGradeChips count={4} />
            </StatSecondarySlot>
          </>
        }
      />
    );
  }

  if (panel === 2) {
    return (
      <StatsTwoColumns
        left={
          <>
            <h3 className="mastery-progress__stats-title">
              <Skel className="mastery-progress-skel--stats-title" style={{ width: "168px" }} />
            </h3>
            <StatSecondarySlot tall>
              <div className="mastery-progress__detail-list">
                {[210, 198, 118, 118].map((width, index) => (
                  <SkeletonStatRow
                    key={index}
                    labelWidth={`${width}px`}
                    valueWidth="24px"
                  />
                ))}
              </div>
            </StatSecondarySlot>
          </>
        }
        right={
          <>
            <h3 className="mastery-progress__stats-title">
              <Skel className="mastery-progress-skel--stats-title" style={{ width: "228px" }} />
            </h3>
            <StatSecondarySlot tall>
              <div className="mastery-progress__review-grade-columns">
                <SkeletonGradeChips count={4} singleColumn />
                <SkeletonGradeChips count={4} singleColumn />
              </div>
            </StatSecondarySlot>
          </>
        }
      />
    );
  }

  return (
    <StatsTwoColumns
      left={
        <>
          <h3 className="mastery-progress__stats-title">
            <Skel className="mastery-progress-skel--stats-title" style={{ width: "168px" }} />
            <Skel className="mastery-progress-skel--info" />
          </h3>
          <SkeletonStatRow labelWidth="36px" valueWidth="78px" />
          <SkeletonMiniBar />
          <StatSecondarySlot>
            <SkeletonStatRow labelWidth="148px" valueWidth="28px" />
          </StatSecondarySlot>
          <p className="mastery-progress__stat-hint">
            <Skel className="mastery-progress-skel--hint" style={{ width: "268px" }} />
            {"\u00a0"}
          </p>
        </>
      }
      right={
        <>
          <h3 className="mastery-progress__stats-title">
            <Skel className="mastery-progress-skel--stats-title" style={{ width: "148px" }} />
            <Skel className="mastery-progress-skel--info" />
          </h3>
          <SkeletonStatRow labelWidth="36px" valueWidth="72px" />
          <SkeletonMiniBar />
          <StatSecondarySlot>
            <SkeletonStatRow labelWidth="138px" valueWidth="28px" />
          </StatSecondarySlot>
          <p className="mastery-progress__stat-hint">
            <Skel className="mastery-progress-skel--hint" style={{ width: "292px" }} />
            {"\u00a0"}
          </p>
          <p className="mastery-progress__stat-hint mastery-progress__regression-line">
            <Skel className="mastery-progress-skel--regression" />
            {"\u00a0"}
          </p>
        </>
      }
    />
  );
}

function MasteryProgressSkeleton() {
  const statsPanel = readStatsPanel();

  return (
    <div className="mastery-progress--skeleton" aria-hidden="true">
      <div className="mastery-progress__bar-track mastery-progress-skel-bar" />

      <div className="mastery-progress__milestones">
        {GAME_LEVELS.map((level, index) => {
          const position =
            GAME_LEVELS.length <= 1 ? 0 : (index / (GAME_LEVELS.length - 1)) * 100;

          return (
            <div
              key={level.id}
              className="mastery-progress__milestone"
              style={{ left: `${position}%` }}
            >
              <div className="mastery-progress__icon-wrap">
                <img
                  src={`${publicUrl}/icons/${level.icon}`}
                  alt=""
                  className="mastery-progress__icon mastery-progress-skel__blason"
                  draggable={false}
                />
              </div>
              <span className="mastery-progress__level-label">
                <Skel className="mastery-progress-skel--level-label" />
              </span>
            </div>
          );
        })}
      </div>

      <p className="mastery-progress__grade">
        <Skel className="mastery-progress-skel--grade" />
      </p>

      <div className="mastery-progress__stats-block">
        <div className="mastery-progress__stats">
          <div className="mastery-progress__stats-content">
            <SkeletonStatsPanel panel={statsPanel} />
          </div>
        </div>

        <div className="mastery-progress__stats-nav">
          {[0, 1, 2].map((index) => (
            <span key={index} className="mastery-progress__stats-radio">
              <span className="mastery-progress-skel mastery-progress-skel--radio" />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatsThresholdTitle({ term, tooltip, validated }) {
  const tooltipId = `mastery-threshold-tooltip-${term}`;

  return (
    <h3 className="mastery-progress__stats-title">
      Seuil de{" "}
      <span
        className="mastery-progress__stats-title-term vocab-tooltip-wrap"
        tabIndex={0}
        aria-describedby={tooltipId}
      >
        {term}
        <span
          id={tooltipId}
          className="vocab-tooltip vocab-tooltip--below mastery-progress__stats-title-tooltip"
          role="tooltip"
        >
          {tooltip}
        </span>
      </span>
      {typeof validated === "boolean" && (
        <>
          {" "}
          <ThresholdStatusIcon validated={validated} />
        </>
      )}
    </h3>
  );
}

export default function MasteryProgressView({
  screen = "progress",
  onUnreadHistoryCountChange = () => {},
}) {
  const [statsPanel, setStatsPanel] = useState(() => readStatsPanel());
  const [initialLoading, setInitialLoading] = useState(true);
  const [progressError, setProgressError] = useState("");
  const [progressData, setProgressData] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyFilter, setHistoryFilter] = useState(() => readHistoryFilter());
  const hasLoadedOnceRef = useRef(false);

  const loadProgress = useCallback(async ({ silent = false } = {}) => {
    if (!silent && !hasLoadedOnceRef.current) {
      setInitialLoading(true);
    }
    setProgressError("");
    try {
      const res = await fetch(`${API_URL}/mastery/progress`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Impossible de charger la progression.");
      }
      setProgressData(data);
      hasLoadedOnceRef.current = true;
    } catch (err) {
      setProgressError(err.message || "Erreur de connexion au serveur.");
      if (!hasLoadedOnceRef.current) {
        setProgressData(null);
      }
    } finally {
      if (!silent && !hasLoadedOnceRef.current) {
        setInitialLoading(false);
      }
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    if (screen !== "history") return undefined;

    let cancelled = false;

    const loadHistory = async () => {
      setHistoryLoading(true);
      setHistoryError("");
      try {
        await syncDailyQuestHistoryIfNeeded();

        const { entries, unreadCount } = await fetchLevelHistory();
        if (cancelled) return;

        if (unreadCount > 0) {
          const marked = await markLevelHistoryAsRead();
          if (cancelled) return;
          setHistoryEntries(marked.entries);
          onUnreadHistoryCountChange(marked.unreadCount);
          return;
        }

        setHistoryEntries(entries);
        onUnreadHistoryCountChange(unreadCount);
      } catch (err) {
        if (!cancelled) {
          setHistoryError(err.message || "Erreur de connexion au serveur.");
          setHistoryEntries([]);
        }
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [screen, onUnreadHistoryCountChange]);

  useEffect(() => {
    if (screen !== "history") return undefined;

    const reloadHistory = () => {
      fetchLevelHistory()
        .then(({ entries, unreadCount }) => {
          setHistoryEntries(entries);
          onUnreadHistoryCountChange(unreadCount);
        })
        .catch(() => {});
    };

    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, reloadHistory);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, reloadHistory);
  }, [screen, onUnreadHistoryCountChange]);

  useEffect(() => {
    if (screen !== "progress") return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && hasLoadedOnceRef.current) {
        loadProgress({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [screen, loadProgress]);

  const validatedLevelCount = progressData?.validatedLevelCount ?? 0;
  const poolSize = progressData?.pool?.poolSize ?? 0;
  const discoveredCount = progressData?.pool?.discoveredCount ?? 0;
  const masteryPercent = progressData?.pool?.masteryPercent ?? 0;
  const masteredCount = progressData?.pool?.masteredCount ?? 0;

  const currentLevelId =
    progressData?.currentLevelId ?? getCurrentLevelId(validatedLevelCount);

  const levelThresholds =
    progressData?.thresholds ?? getMasteryThresholdsForLevel(currentLevelId);
  const discoveryTargetRate = levelThresholds.discovery;
  const masteryTargetRate = levelThresholds.mastery;

  const currentLevelProgress =
    progressData?.currentLevelProgress ??
    computeCurrentLevelProgress(poolSize, discoveredCount, masteryPercent, levelThresholds);
  const progress =
    progressData?.overallProgress ??
    computeOverallProgress(validatedLevelCount, currentLevelProgress);
  const progressPercent =
    progressData?.overallProgressPercent ?? Math.round(progress * 100);

  const levelsToShow = GAME_LEVELS;

  const currentLevel =
    GAME_LEVELS.find((level) => level.id === currentLevelId) || GAME_LEVELS[0];

  const remainingCount =
    progressData?.pool?.remainingDiscovery ?? Math.max(0, poolSize - discoveredCount);
  const remainingToDiscoveryTarget =
    progressData?.pool?.remainingToDiscoveryTarget ??
    Math.max(0, Math.ceil(poolSize * discoveryTargetRate - 1e-9) - discoveredCount);
  const remainingToMasteryTarget =
    progressData?.pool?.remainingToMasteryTarget ??
    Math.max(0, Math.ceil(poolSize * masteryTargetRate - 1e-9) - masteredCount);
  const discoveryTargetCount =
    progressData?.pool?.discoveryTargetCount ??
    Math.ceil(poolSize * discoveryTargetRate - 1e-9);
  const masteryTargetCount =
    progressData?.pool?.masteryTargetCount ??
    Math.ceil(poolSize * masteryTargetRate - 1e-9);
  const masteredGradeCounts = progressData?.pool?.masteredGradeCounts ?? {};
  const noEwssBreakdown = progressData?.pool?.noEwssBreakdown ?? {};
  const reviewGradeCounts = progressData?.pool?.reviewGradeCounts ?? {};
  const reviewGradeTotal = sumReviewGradeCounts(reviewGradeCounts);
  const discoveryPercent =
    progressData?.pool?.discoveryPercent ??
    (poolSize > 0 ? Math.round((discoveredCount / poolSize) * 100) : 0);
  const discoveryTargetPercent = Math.round(discoveryTargetRate * 100);
  const masteryTargetPercent = Math.round(masteryTargetRate * 100);
  const discoveryThresholdPercent =
    discoveryTargetCount > 0
      ? Math.min(100, Math.round((discoveredCount / discoveryTargetCount) * 100))
      : 0;
  const masteryThresholdPercent =
    masteryTargetCount > 0
      ? Math.min(100, Math.round((masteredCount / masteryTargetCount) * 100))
      : 0;
  const discoveryThresholdReached =
    discoveryTargetCount > 0 && discoveredCount >= discoveryTargetCount;
  const masteryThresholdReached =
    masteryTargetCount > 0 && masteredCount >= masteryTargetCount;
  const regressionTargetCount = progressData?.masteryRegressionTarget ?? null;
  const showRegressionThreshold =
    validatedLevelCount >= 1 &&
    Number.isFinite(regressionTargetCount) &&
    regressionTargetCount > 0;

  const statsPanels = [
    {
      left: (
        <>
          <h3 className="mastery-progress__stats-title">Découverte du pool</h3>
          <div className="mastery-progress__stat-row">
            <span className="mastery-progress__stat-label">Mots découverts</span>
            <span className="mastery-progress__stat-value">
              {discoveredCount} / {poolSize}
            </span>
          </div>
          <MiniStatBar
            value={discoveredCount}
            max={poolSize}
            label="Progression de découverte"
          />
          <StatSecondarySlot>
            <div className="mastery-progress__stat-row">
              <span className="mastery-progress__stat-label">Reste à découvrir</span>
              <span className="mastery-progress__stat-value">{remainingCount}</span>
            </div>
          </StatSecondarySlot>
          <p className="mastery-progress__stat-hint">
            {discoveryPercent} % du pool rencontré en jeu
          </p>
        </>
      ),
      right: (
        <>
          <h3 className="mastery-progress__stats-title">Maîtrise du pool</h3>
          <div className="mastery-progress__stat-row">
            <span className="mastery-progress__stat-label">Mots maîtrisés</span>
            <span className="mastery-progress__stat-value">
              {masteredCount} / {poolSize}
            </span>
          </div>
          <MiniStatBar
            value={masteredCount}
            max={poolSize}
            label="Progression de maîtrise du pool"
          />
          <StatSecondarySlot tall>
            <MasteredGradeBreakdown counts={masteredGradeCounts} />
          </StatSecondarySlot>
        </>
      ),
    },
    {
      left: (
        <>
          <StatsThresholdTitle
            term="découverte"
            tooltip={`Objectif atteint si ${discoveryTargetPercent} % des mots du pool ont été découverts.`}
            validated={discoveryThresholdReached}
          />
          <div className="mastery-progress__stat-row">
            <span className="mastery-progress__stat-label">{discoveryThresholdPercent} %</span>
            <span className="mastery-progress__stat-value">
              {discoveredCount} / {discoveryTargetCount}
            </span>
          </div>
          <MiniStatBar
            value={discoveredCount}
            max={discoveryTargetCount}
            label="Progression vers le seuil de découverte"
          />
          <StatSecondarySlot>
            <div className="mastery-progress__stat-row">
              <span className="mastery-progress__stat-label">Restant à découvrir</span>
              <span className="mastery-progress__stat-value">{remainingToDiscoveryTarget}</span>
            </div>
          </StatSecondarySlot>
          <p className="mastery-progress__stat-hint">
            {discoveryTargetCount} mots à découvrir pour valider le niveau suivant
          </p>
        </>
      ),
      right: (
        <>
          <StatsThresholdTitle
            term="maîtrise"
            tooltip={`Objectif atteint si ${masteryTargetPercent} % des mots du pool sont maîtrisés.`}
            validated={masteryThresholdReached}
          />
          <div className="mastery-progress__stat-row">
            <span className="mastery-progress__stat-label">{masteryThresholdPercent} %</span>
            <span className="mastery-progress__stat-value">
              {masteredCount} / {masteryTargetCount}
            </span>
          </div>
          <MiniStatBar
            value={masteredCount}
            max={masteryTargetCount}
            label="Progression vers le seuil de maîtrise"
          />
          <StatSecondarySlot>
            <div className="mastery-progress__stat-row">
              <span className="mastery-progress__stat-label">Restant à maîtriser</span>
              <span className="mastery-progress__stat-value">{remainingToMasteryTarget}</span>
            </div>
          </StatSecondarySlot>
          <p className="mastery-progress__stat-hint">
            {masteryTargetCount} mots maîtrisés requis pour valider le niveau suivant
          </p>
          <p
            className={`mastery-progress__stat-hint mastery-progress__regression-line${
              !showRegressionThreshold ? " mastery-progress__regression-line--empty" : ""
            }`}
          >
            {showRegressionThreshold ? (
              <>
                Seuil de{" "}
                <span
                  className="mastery-progress__stats-title-term vocab-tooltip-wrap"
                  tabIndex={0}
                  aria-describedby="mastery-regression-tooltip"
                >
                  régression
                  <span
                    id="mastery-regression-tooltip"
                    className="vocab-tooltip vocab-tooltip--below mastery-progress__stats-title-tooltip"
                    role="tooltip"
                  >
                    Seuil en dessous duquel vous régressez au niveau inférieur.
                  </span>
                </span>
                {" "}
                : {regressionTargetCount}
              </>
            ) : (
              <span aria-hidden="true">&nbsp;</span>
            )}
          </p>
        </>
      ),
    },
    {
      left: (
        <>
          <h3 className="mastery-progress__stats-title">Mots sans score EWSS</h3>
          <StatSecondarySlot tall>
            <NoEwssBreakdown counts={noEwssBreakdown} />
          </StatSecondarySlot>
        </>
      ),
      right: (
        <>
          <h3 className="mastery-progress__stats-title">
            Mots à revoir (grade &lt; B+)
            <span className="mastery-progress__stats-title-suffix">
              {" "}
              <span className="mastery-progress__stats-title-diamond" aria-hidden="true">
                ◆
              </span>{" "}
              {reviewGradeTotal}
            </span>
          </h3>
          <StatSecondarySlot tall>
            <ReviewGradeBreakdown counts={reviewGradeCounts} />
          </StatSecondarySlot>
        </>
      ),
    },
  ];

  const activeStats = statsPanels[statsPanel] || statsPanels[0];

  const showInitialLoading = initialLoading && !progressData;
  const filteredHistoryEntries = filterHistoryEntries(historyEntries, historyFilter);
  const isProgressScreen = screen !== "history";

  const screenTitle =
    screen === "history"
      ? "Observateur d'évènements"
      : "Progression de votre niveau de maîtrise";

  return (
    <div
      className={`mastery-progress${
        isProgressScreen ? " mastery-progress--progress" : ""
      }${screen === "history" ? " mastery-progress--history" : ""}`}
    >
      <header
        className={`mastery-progress__header${
          screen === "history" ? " mastery-progress__header--history" : ""
        }`}
      >
        {screen === "history" ? (
          <div className="level-history__header-row">
            <h2 className="mastery-progress__title">{screenTitle}</h2>
            <div className="level-history__filter-wrap">
              <span className="level-history__filter-label">Filtrer</span>
              <HistoryFilterDropdown
                value={historyFilter}
                onChange={setHistoryFilter}
              />
            </div>
          </div>
        ) : (
          <h2 className="mastery-progress__title">{screenTitle}</h2>
        )}
      </header>

      <div className="mastery-progress__layout">
        <div className="mastery-progress__panel">
          {isProgressScreen ? (
            <div
              className={`mastery-progress__bar-wrap${
                showInitialLoading ? " mastery-progress__bar-wrap--skeleton" : ""
              }`}
            >
              {progressError && (
                <p className="mastery-progress__error" role="alert">
                  {progressError}
                </p>
              )}
              {showInitialLoading ? (
                <MasteryProgressSkeleton />
              ) : (
                <>
                  <div
                    className="mastery-progress__bar-track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progressPercent}
                    aria-label="Progression de maîtrise"
                  >
                    <div
                      className="mastery-progress__bar-fill"
                      style={{ width: `${progress * 100}%` }}
                    />
                    <div className="mastery-progress__bar-shine" aria-hidden="true" />
                  </div>

                  <div className="mastery-progress__milestones">
                    {levelsToShow.map((level) => {
                      const state = getLevelState(
                        level.id,
                        validatedLevelCount,
                        currentLevelId
                      );
                      const index = GAME_LEVELS.findIndex((entry) => entry.id === level.id);
                      const position =
                        GAME_LEVELS.length <= 1
                          ? 0
                          : (index / (GAME_LEVELS.length - 1)) * 100;

                      return (
                        <div
                          key={level.id}
                          className={`mastery-progress__milestone mastery-progress__milestone--${state} vocab-tooltip-wrap${
                            level.id === currentLevelId
                              ? " mastery-progress__milestone--active"
                              : ""
                          }`}
                          style={{ left: `${position}%` }}
                        >
                          <div className="mastery-progress__icon-wrap">
                            {level.id === currentLevelId && (
                              <div
                                className="mastery-progress__lottie-wrap"
                                aria-hidden="true"
                              >
                                <Lottie
                                  animationData={starsAndLightAnimationData}
                                  loop
                                  autoplay
                                  className="mastery-progress__lottie"
                                />
                              </div>
                            )}
                            <img
                              src={`${publicUrl}/icons/${level.icon}`}
                              alt=""
                              className="mastery-progress__icon"
                              loading="lazy"
                              draggable={false}
                              aria-hidden="true"
                            />
                          </div>
                          <span className="mastery-progress__level-label">Niveau {level.id}</span>
                          <span
                            className="vocab-tooltip vocab-tooltip--wide mastery-progress__milestone-tooltip"
                            role="tooltip"
                          >
                            {level.name}. {level.description}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <p className="mastery-progress__grade">
                    <span className="mastery-progress__grade-label">Votre grade actuel : </span>
                    <span className="mastery-progress__grade-name">{currentLevel.name}</span>
                  </p>

                  <div className="mastery-progress__stats-block">
                    <div className="mastery-progress__stats">
                      <div className="mastery-progress__stats-content">
                        <StatsTwoColumns
                          left={activeStats.left}
                          right={activeStats.right}
                        />
                      </div>
                    </div>

                    <div
                      className="mastery-progress__stats-nav"
                      role="radiogroup"
                      aria-label="Indicateurs du niveau"
                    >
                      {statsPanels.map((_, index) => (
                        <label key={index} className="mastery-progress__stats-radio">
                          <input
                            type="radio"
                            name="mastery-stats-panel"
                            className="mastery-progress__stats-radio-input"
                            checked={statsPanel === index}
                            onChange={() => {
                              setStatsPanel(index);
                              writeStatsPanel(index);
                            }}
                          />
                          <span className="mastery-progress__stats-radio-dot" />
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : screen === "history" ? (
            <div className="mastery-progress__history-wrap">
              <LevelHistoryView
                entries={filteredHistoryEntries}
                loading={historyLoading}
                error={historyError}
                filter={historyFilter}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
